import { deriveDistrictKey } from './districtGeoid.js'
import { VA_GENERAL_2026 as roster } from '../data/vaGeneral2026.js'

const normalize = value => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
const inactive = new Set(['withdrawn', 'disqualified', 'deceased', 'lost'])

const HOUSE = /^u\.?s\.? house(?: of representatives)?$/i
const SENATE = /^u\.?s\.? senate$/i

const isHouse = row => HOUSE.test(String(row?.office ?? '').trim())
const isSenate = row => SENATE.test(String(row?.office ?? '').trim())

export function electionToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

/** Contests this voter actually votes in. No districts selected means the
 *  whole-state guest view, which reconciles every reviewed contest. */
function contestsFor(voter) {
  if (normalize(voter?.state) !== 'va') return []
  const keys = Array.isArray(voter?.districtKeys) ? voter.districtKeys.filter(Boolean) : []
  if (keys.length === 0) return roster.contests
  return roster.contests.filter(contest => contest.statewide || keys.includes(contest.districtKey))
}

/** A row belongs to one of these contests, so it must be either selected by
 *  explicit identity or dropped. Districts the voter does not vote in are not
 *  this function's business and pass through untouched. */
function inContest(row, contests, ids) {
  if (ids.has(row?.id)) return true
  if (normalize(row?.state) !== 'va') return false
  if (isSenate(row)) return contests.some(contest => contest.statewide)
  const keys = [row?.district_geoid, deriveDistrictKey(row).key].filter(Boolean)
  return contests.some(contest => contest.districtKey && keys.includes(contest.districtKey))
}

function matchesIdentity(row, entry, contest) {
  if (normalize(row.state) !== 'va') return false
  if (String(row.election_year) !== String(roster.electionYear)) return false
  if (inactive.has(normalize(row.candidate_status))) return false
  if (!entry.names.some(name => normalize(name) === normalize(row.name))) return false

  // Production stores this as fec_candidate_id; fec_id is the shape the
  // ingestion fixtures use. Checking only one of them silently disables the
  // guard against whichever lineage uses the other.
  const fec = row.fec_candidate_id ?? row.fec_id
  if (entry.fecId && fec && fec !== entry.fecId) return false

  if (contest.statewide) return isSenate(row)
  if (!isHouse(row)) return false
  if (deriveDistrictKey(row).key !== contest.districtKey) return false
  return !row.district_geoid || row.district_geoid === contest.districtKey
}

/**
 * One row per reviewed person, for pages that LIST candidates rather than
 * assert a ballot.
 *
 * WHY THIS IS NOT reviewedCandidatesFor
 *
 * Two lineages exist per Virginia candidate - an evidence-bearing UUID row and
 * an older slug row - and both are real rows in `candidates`. A page that
 * renders the raw list therefore offers the same person twice, under two
 * spellings and sometimes two different party strings. Measured on 2026-11-03
 * against both live environments: all four certified VA-01/VA-06 candidates
 * appeared twice in /compare, and Robert Wittman's canonical option read
 * "(Unknown)" beside an alias option reading "(Republican)".
 *
 * reviewedCandidatesFor is the wrong instrument for that. It answers "what is
 * on this voter's ballot", so outside the reviewed window - which closes the
 * day after the election - it correctly reports every reviewed entry as
 * unavailable and returns NO Virginia federal candidates at all. That is the
 * honest answer to its question and the wrong one for a directory: on
 * 2026-11-04 it would empty the comparison selector of every Virginia federal
 * candidate instead of merely de-duplicating it.
 *
 * So this function can only ever REMOVE A DUPLICATE. It never drops a person,
 * never merges by name, never copies a field between rows, and never asserts
 * anything about a ballot. Rows whose stored identity no longer matches the
 * reviewed entry are left exactly where they are, visible: if we cannot vouch
 * that two rows are the same person, we do not hide one of them.
 *
 * @param {Array} candidates rows as stored
 * @returns {{candidates: Array, resolveId: (id: string) => string, supersededBy: Map<string,string>}}
 *          `resolveId` maps a superseded row's id onto the surviving one, so a
 *          link someone already holds keeps working.
 */
export function reviewedDirectory(candidates) {
  const rows = Array.isArray(candidates) ? candidates : []
  const byId = new Map()
  for (const row of rows) {
    if (row && typeof row.id === 'string' && row.id && !byId.has(row.id)) byId.set(row.id, row)
  }

  const supersededBy = new Map()
  for (const contest of roster.contests) {
    for (const entry of contest.entries) {
      // entry.ids is an explicit, inspected list and is ordered canonical
      // first, so the evidence-bearing row is the one that survives.
      const present = entry.ids.map(id => byId.get(id)).filter(row => row && matchesIdentity(row, entry, contest))
      if (present.length < 2) continue
      for (const duplicate of present.slice(1)) supersededBy.set(duplicate.id, present[0].id)
    }
  }

  return {
    candidates: supersededBy.size === 0 ? rows : rows.filter(row => !supersededBy.has(row?.id)),
    resolveId: id => supersededBy.get(id) ?? id,
    supersededBy,
  }
}

/**
 * Read-time, source-reviewed participation overlay. No writes, name-based
 * merges, or evidence transfers. Contests the voter does not vote in keep
 * their existing behavior; this does not certify an entire ballot. A
 * missing/conflicting identity stays unavailable.
 */
export function reviewedCandidatesFor(candidates, voter, { asOf = electionToday() } = {}) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(asOf) || !Number.isFinite(Date.parse(asOf)) || new Date(asOf).toISOString().slice(0, 10) !== asOf) {
    throw new Error('A valid election review date is required')
  }
  const rows = Array.isArray(candidates) ? candidates : []
  const seen = new Set()
  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !row.id || seen.has(row.id)) throw new Error('Candidate identities must be unique')
    seen.add(row.id)
  }

  const contests = contestsFor(voter)
  if (contests.length === 0) return { candidates: rows, notice: null }

  const available = asOf >= roster.verifiedOn && asOf <= roster.electionDate
  const ids = new Set(contests.flatMap(contest => contest.entries.flatMap(entry => entry.ids)))
  const selected = []
  const missing = []

  for (const contest of contests) {
    for (const entry of contest.entries) {
      const aliases = entry.ids.map(id => rows.find(row => row.id === id)).filter(Boolean)
      // A stale duplicate must not resurrect an explicitly inactive/conflicting identity.
      if (!available || aliases.length === 0 || aliases.some(row => !matchesIdentity(row, entry, contest))) {
        missing.push(entry.name)
        continue
      }
      // Copy only the reviewed geography into the display object. Retain the
      // selected row's own ID and evidence so profile/detail and matching
      // remain consistent. A statewide seat has no district to copy.
      selected.push(contest.statewide ? { ...aliases[0] } : { ...aliases[0], district_geoid: contest.districtKey })
    }
  }

  return {
    candidates: [...selected, ...rows.filter(row => !inContest(row, contests, ids))],
    notice: { available, missing, sourceUrl: roster.sourceUrl, verifiedOn: roster.verifiedOn, electionDate: roster.electionDate },
  }
}
